// src/hooks/useTokenCountdown.js
import { useEffect, useMemo, useRef, useState } from 'react';
import { getStoredAccessToken, getTokenExpiration } from '../utils/tokenStorage';
import { getKeycloak, isKeycloakMode } from '../auth/keycloakClient';

const hasWindow = typeof window !== 'undefined';

function readTokenState() {
    // In Keycloak mode, the access token is managed in-memory by keycloak-js, not in storage.
    if (isKeycloakMode()) {
        const kc = getKeycloak();
        const token = kc?.token || null;
        const expSeconds = kc?.tokenParsed?.exp;
        if (!token) {
            return {
                hasToken: false,
                expiration: null,
                timeLeftMs: null,
            };
        }
        const expiration = typeof expSeconds === 'number'
            ? expSeconds * 1000
            : getTokenExpiration(token);
        if (!expiration) {
            return {
                hasToken: true,
                expiration: null,
                timeLeftMs: null,
            };
        }
        const diff = expiration - Date.now();
        return {
            hasToken: true,
            expiration,
            timeLeftMs: diff > 0 ? diff : 0,
        };
    }

    const token = getStoredAccessToken();
    if (!token) {
        return {
            hasToken: false,
            expiration: null,
            timeLeftMs: null,
        };
    }

    const expiration = getTokenExpiration(token);
    if (!expiration) {
        return {
            hasToken: true,
            expiration: null,
            timeLeftMs: null,
        };
    }

    const diff = expiration - Date.now();
    return {
        hasToken: true,
        expiration,
        timeLeftMs: diff > 0 ? diff : 0,
    };
}

export function useTokenCountdown(options = {}) {
    const { onExpire, enabled = true } = options;
    const [state, setState] = useState(() => readTokenState());
    const hasFiredRef = useRef(false);

    useEffect(() => {
        if (!hasWindow || !enabled) {
            return undefined;
        }

        const tick = () => {
            const nextState = readTokenState();
            setState((prev) => {
                if (
                    prev.hasToken !== nextState.hasToken ||
                    prev.timeLeftMs !== nextState.timeLeftMs ||
                    prev.expiration !== nextState.expiration
                ) {
                    return nextState;
                }
                return prev;
            });

            if (nextState.hasToken && nextState.timeLeftMs !== null && nextState.timeLeftMs <= 0) {
                if (!hasFiredRef.current && typeof onExpire === 'function') {
                    hasFiredRef.current = true;
                    onExpire();
                }
            } else if (nextState.timeLeftMs === null || nextState.timeLeftMs > 0) {
                hasFiredRef.current = false;
            }
        };

        tick();
        const intervalId = window.setInterval(tick, 1000);

        // Only relevant in legacy mode where tokens live in Web Storage.
        let handleStorage;
        if (!isKeycloakMode()) {
            handleStorage = (event) => {
                if (event.key === 'authToken' || event.key === 'authRefreshToken') {
                    tick();
                }
            };
            window.addEventListener('storage', handleStorage);
        }

        return () => {
            window.clearInterval(intervalId);
            if (handleStorage) {
                window.removeEventListener('storage', handleStorage);
            }
        };
    }, [enabled, onExpire]);

    useEffect(() => {
        hasFiredRef.current = false;
    }, [state.expiration]);

    const formatted = useMemo(() => {
        if (state.timeLeftMs === null) {
            return null;
        }

        const totalSeconds = Math.max(0, Math.floor(state.timeLeftMs / 1000));
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        const minuteString = String(minutes).padStart(2, '0');
        const secondString = String(seconds).padStart(2, '0');

        if (hours > 0) {
            return `${hours}:${minuteString}:${secondString}`;
        }

        return `${minuteString}:${secondString}`;
    }, [state.timeLeftMs]);

    const expiresAt = useMemo(() => {
        if (!state.expiration) {
            return null;
        }
        return new Date(state.expiration);
    }, [state.expiration]);

    const isExpired = state.hasToken && state.timeLeftMs === 0;

    return {
        ...state,
        formatted,
        expiresAt,
        isExpired,
    };
}

export default useTokenCountdown;
